import { useCallback, useEffect, useRef, useState } from 'react';
import {
  MedsiWebEvents,
  RPPG_CHUNK_DURATION_MS,
  subscribe,
  useWebRecording,
  type FaceValidationEvent,
  type RecordingChunkReadyEvent,
} from '@Medsicheck/medsi-web-sdk';

import {
  RPPG_ABORT_RECOMMENDED_STREAK,
  RPPG_QUICK_VITALS_INTERVAL_MS,
  RPPG_QUICK_VITALS_START_CHUNK,
  RPPG_SCAN_DURATION_MS,
  RPPG_VITAL_PREDICT_CHUNK,
  rppgQuickVitals,
  rppgResolveFinalResult,
  rppgUploadInit,
  uploadChunkWithRetry,
  type RppgCaptureQuality,
  type RppgDemographics,
  type RppgFinalResult,
  type RppgQualityReasonCode,
  type RppgQuickVitals,
  type RppgUploadInitResponse,
  type RppgUploadMetadata,
} from 'services/rppg/api';
import { getMedsiScanConfig } from 'lib/medsi/config';
import type {
  RppgAbortReasonCode,
  RppgScanAbortSource,
} from 'lib/medsi/device-health';
import { MEDSI_SCAN_ERROR_KEYS } from 'lib/medsi/messages';

export type ScanPhase =
  | 'idle'
  | 'initializing'
  | 'scanning'
  | 'processing'
  | 'done'
  | 'error';

export type RppgQualityAbortSource = RppgScanAbortSource;

export interface RppgQualityAbort {
  reasons: RppgAbortReasonCode[];
  source: RppgQualityAbortSource;
  readingId: string;
}

export interface RppgOrchestratorState {
  phase: ScanPhase;
  readingId: string | null;
  chunkCount: number;
  uploadedChunkCount: number;
  progress: number;
  liveVitals: RppgQuickVitals | null;
  captureQuality: RppgCaptureQuality | null;
  qualityAbort: RppgQualityAbort | null;
  /** Mid-scan abort_recommended warning; recording keeps running until the user chooses. */
  qualityContinuePrompt: RppgQualityAbort | null;
  finalResult: RppgFinalResult | null;
  error: string | null;
  processingProgress: number;
  imageValidityCounts: Record<string, number>;
  faceValidity: FaceValidationEvent | null;
}

export interface RppgOrchestratorApi extends RppgOrchestratorState {
  start: (demographics: RppgDemographics) => Promise<void>;
  stopScan: () => Promise<void>;
  reset: () => void;
  prefetchUploadInit: (metadata?: RppgUploadMetadata) => void;
  abortScan: (
    reasons: RppgAbortReasonCode[],
    source: RppgQualityAbortSource,
  ) => void;
  clearQualityAbort: () => void;
  clearQualityContinuePrompt: () => void;
  continueScanDespiteAbort: () => void;
}

const INITIAL_STATE: RppgOrchestratorState = {
  phase: 'idle',
  readingId: null,
  chunkCount: 0,
  uploadedChunkCount: 0,
  progress: 0,
  liveVitals: null,
  captureQuality: null,
  qualityAbort: null,
  qualityContinuePrompt: null,
  finalResult: null,
  error: null,
  processingProgress: 0,
  imageValidityCounts: {},
  faceValidity: null,
};

function createReadingId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `reading-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function getRescanRecommendation(result: RppgFinalResult): {
  recommended: boolean;
  reasons: RppgQualityReasonCode[];
} | null {
  const rescan = result.quality?.rescan;
  if (!rescan?.recommended) {
    return null;
  }
  return {
    recommended: true,
    reasons: (rescan.reasons ?? []) as RppgQualityReasonCode[],
  };
}

function isAbortRecommended(value: unknown): boolean {
  return value === true || value === 1 || value === "1" || value === "true";
}

export function useRppgScanOrchestrator(): RppgOrchestratorApi {
  const recording = useWebRecording();
  const [state, setState] = useState<RppgOrchestratorState>(INITIAL_STATE);

  const isRunningRef = useRef(false);
  const isScanEndingRef = useRef(false);
  const chunkCountRef = useRef(0);
  const uploadedChunkCountRef = useRef(0);
  const readingIdRef = useRef('');
  const prefetchedReadingIdRef = useRef('');
  const demographicsRef = useRef<RppgDemographics>({});
  const initResponseRef = useRef<RppgUploadInitResponse | null>(null);
  const uploadInitPromiseRef =
    useRef<Promise<RppgUploadInitResponse> | null>(null);
  const uploadMetadataRef = useRef<RppgUploadMetadata | undefined>(undefined);
  const vitalPredictFiredRef = useRef(false);
  const pendingFinalResultRef = useRef<RppgFinalResult | null>(null);
  const quickVitalsTimerRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  const uploadQueueRef = useRef(Promise.resolve());
  const predictErrorRef = useRef<string | null>(null);
  const stoppingRef = useRef(false);
  const qualityAbortedRef = useRef(false);
  const abortRecommendedStreakRef = useRef(0);
  const ignoreAbortRecommendedRef = useRef(false);
  const qualityContinuePromptRef = useRef(false);

  const updateState = useCallback((patch: Partial<RppgOrchestratorState>) => {
    setState(prev => ({ ...prev, ...patch }));
  }, []);

  const clearQuickVitalsTimer = useCallback(() => {
    if (quickVitalsTimerRef.current) {
      clearInterval(quickVitalsTimerRef.current);
      quickVitalsTimerRef.current = null;
    }
  }, []);

  const clearQualityAbort = useCallback(() => {
    qualityAbortedRef.current = false;
    abortRecommendedStreakRef.current = 0;
    qualityContinuePromptRef.current = false;
    updateState({ qualityAbort: null, qualityContinuePrompt: null });
  }, [updateState]);

  const clearQualityContinuePrompt = useCallback(() => {
    qualityContinuePromptRef.current = false;
    updateState({ qualityContinuePrompt: null });
  }, [updateState]);

  const continueScanDespiteAbort = useCallback(() => {
    ignoreAbortRecommendedRef.current = true;
    abortRecommendedStreakRef.current = 0;
    qualityContinuePromptRef.current = false;
    updateState({ qualityContinuePrompt: null });
  }, [updateState]);

  const abortScan = useCallback(
    (reasons: RppgAbortReasonCode[], source: RppgQualityAbortSource) => {
      if (qualityAbortedRef.current) {
        return;
      }
      qualityAbortedRef.current = true;

      const abortReadingId = readingIdRef.current;
      isRunningRef.current = false;
      isScanEndingRef.current = true;
      abortRecommendedStreakRef.current = 0;
      qualityContinuePromptRef.current = false;
      clearQuickVitalsTimer();

      updateState({
        phase: "idle",
        progress: 0,
        processingProgress: 0,
        liveVitals: null,
        captureQuality: null,
        finalResult: null,
        error: null,
        qualityContinuePrompt: null,
        qualityAbort: {
          reasons: reasons ?? [],
          source,
          readingId: abortReadingId,
        },
      });

      void recording.stop().finally(() => {
        isScanEndingRef.current = false;
      });
    },
    [clearQuickVitalsTimer, recording, updateState],
  );

  const ensureUploadInit = useCallback(
    async (rid: string): Promise<RppgUploadInitResponse> => {
      if (initResponseRef.current) {
        return initResponseRef.current;
      }

      const { rppgApiKey, rppgBaseUrl } = getMedsiScanConfig();

      if (!uploadInitPromiseRef.current) {
        uploadInitPromiseRef.current = rppgUploadInit(
          rppgApiKey,
          rppgBaseUrl,
          rid,
          uploadMetadataRef.current,
        )
          .then(response => {
            initResponseRef.current = response;
            readingIdRef.current = response.reading_id;
            updateState({ readingId: response.reading_id });
            return response;
          })
          .catch(err => {
            uploadInitPromiseRef.current = null;
            throw err;
          });
      }

      return uploadInitPromiseRef.current;
    },
    [updateState],
  );

  const prefetchUploadInit = useCallback(
    (metadata?: RppgUploadMetadata) => {
      if (initResponseRef.current || uploadInitPromiseRef.current) {
        return;
      }

      uploadMetadataRef.current = metadata;
      const { rppgApiKey, rppgBaseUrl } = getMedsiScanConfig();
      const rid = createReadingId();
      prefetchedReadingIdRef.current = rid;
      uploadInitPromiseRef.current = rppgUploadInit(
        rppgApiKey,
        rppgBaseUrl,
        rid,
        metadata,
      )
        .then(response => {
          initResponseRef.current = response;
          prefetchedReadingIdRef.current = response.reading_id;
          readingIdRef.current = response.reading_id;
          updateState({ readingId: response.reading_id });
          return response;
        })
        .catch(err => {
          uploadInitPromiseRef.current = null;
          prefetchedReadingIdRef.current = '';
          console.warn('[rPPG] prefetch upload-init failed:', err);
          throw err;
        });
    },
    [updateState],
  );

  const completeWithFinalResult = useCallback(
    (result: RppgFinalResult) => {
      const rescan = getRescanRecommendation(result);
      if (
        rescan?.recommended &&
        !ignoreAbortRecommendedRef.current &&
        !qualityContinuePromptRef.current
      ) {
        abortScan(rescan.reasons, 'vital_predict');
        return;
      }

      pendingFinalResultRef.current = result;
      if (!isRunningRef.current) {
        updateState({
          phase: 'done',
          finalResult: result,
          processingProgress: 1,
          error: null,
        });
      }
    },
    [abortScan, updateState],
  );

  const scheduleQuickVitalsPoll = useCallback(() => {
    if (quickVitalsTimerRef.current) {
      return;
    }

    const pollQuickVitals = async () => {
      if (!isRunningRef.current || qualityAbortedRef.current) {
        clearQuickVitalsTimer();
        return;
      }

      const { rppgApiKey, rppgBaseUrl } = getMedsiScanConfig();
      const envelope = await rppgQuickVitals(
        rppgApiKey,
        rppgBaseUrl,
        readingIdRef.current,
        chunkCountRef.current,
      );

      if (!envelope || qualityAbortedRef.current || !isRunningRef.current) {
        return;
      }

      const captureQuality = envelope.quality?.capture_quality ?? null;

      updateState({
        liveVitals: envelope.predictions,
        captureQuality,
      });

      if (!captureQuality) {
        return;
      }

      if (
        ignoreAbortRecommendedRef.current ||
        qualityContinuePromptRef.current
      ) {
        abortRecommendedStreakRef.current = 0;
        return;
      }

      abortRecommendedStreakRef.current = isAbortRecommended(
        captureQuality.abort_recommended,
      )
        ? abortRecommendedStreakRef.current + 1
        : 0;

      if (
        abortRecommendedStreakRef.current >= RPPG_ABORT_RECOMMENDED_STREAK
      ) {
        console.warn("[rPPG] quality continue prompt", {
          streak: abortRecommendedStreakRef.current,
          reasons: captureQuality.reasons,
        });
        qualityContinuePromptRef.current = true;
        abortRecommendedStreakRef.current = 0;
        updateState({
          qualityContinuePrompt: {
            reasons: captureQuality.reasons ?? [],
            source: "quick_vitals",
            readingId: readingIdRef.current,
          },
        });
      }
    };

    void pollQuickVitals();
    quickVitalsTimerRef.current = setInterval(
      pollQuickVitals,
      RPPG_QUICK_VITALS_INTERVAL_MS,
    );
  }, [clearQuickVitalsTimer, updateState]);

  const fireVitalPredict = useCallback(async () => {
    if (vitalPredictFiredRef.current) {
      return;
    }
    vitalPredictFiredRef.current = true;

    const uploaded = uploadedChunkCountRef.current;
    if (uploaded < RPPG_QUICK_VITALS_START_CHUNK) {
      predictErrorRef.current = MEDSI_SCAN_ERROR_KEYS.CHUNK_UPLOAD_FAILED;
      if (!isRunningRef.current) {
        updateState({
          phase: 'error',
          error: predictErrorRef.current,
        });
      }
      return;
    }

    if (!isRunningRef.current) {
      updateState({ processingProgress: 0.1 });
    }

    const { rppgApiKey, rppgBaseUrl } = getMedsiScanConfig();
    const result = await rppgResolveFinalResult(
      rppgApiKey,
      rppgBaseUrl,
      readingIdRef.current,
      demographicsRef.current,
      chunkCountRef.current,
    );

    if (!result) {
      predictErrorRef.current = MEDSI_SCAN_ERROR_KEYS.VITAL_PREDICT_TIMEOUT;
      if (!isRunningRef.current) {
        updateState({
          phase: 'error',
          error: predictErrorRef.current,
          processingProgress: 1,
        });
      }
      return;
    }

    completeWithFinalResult(result);
  }, [completeWithFinalResult, updateState]);

  const transitionToProcessing = useCallback(async () => {
    if (isScanEndingRef.current) {
      return;
    }
    isScanEndingRef.current = true;
    isRunningRef.current = false;
    clearQuickVitalsTimer();

    await recording.stop();
    await uploadQueueRef.current;

    if (predictErrorRef.current) {
      updateState({
        phase: 'error',
        error: predictErrorRef.current,
      });
      return;
    }

    if (pendingFinalResultRef.current) {
      const pending = pendingFinalResultRef.current;
      const rescan = getRescanRecommendation(pending);
      if (rescan?.recommended && !ignoreAbortRecommendedRef.current) {
        abortScan(rescan.reasons, 'vital_predict');
        return;
      }
      updateState({
        phase: 'done',
        finalResult: pending,
        processingProgress: 1,
        error: null,
      });
      return;
    }

    updateState({
      phase: 'processing',
      processingProgress: 0.35,
    });

    if (!vitalPredictFiredRef.current) {
      await fireVitalPredict();
      return;
    }

    const deadline = Date.now() + 90_000;
    while (
      !pendingFinalResultRef.current &&
      !predictErrorRef.current &&
      Date.now() < deadline
    ) {
      await new Promise<void>(resolve => setTimeout(resolve, 500));
    }

    if (pendingFinalResultRef.current) {
      const pending = pendingFinalResultRef.current;
      const rescan = getRescanRecommendation(pending);
      if (rescan?.recommended && !ignoreAbortRecommendedRef.current) {
        abortScan(rescan.reasons, 'vital_predict');
        return;
      }
      updateState({
        phase: 'done',
        finalResult: pending,
        processingProgress: 1,
        error: null,
      });
    } else if (predictErrorRef.current) {
      updateState({
        phase: 'error',
        error: predictErrorRef.current,
      });
    } else {
      updateState({
        phase: 'error',
        error: MEDSI_SCAN_ERROR_KEYS.VITAL_PREDICT_TIMEOUT,
      });
    }
  }, [abortScan, clearQuickVitalsTimer, fireVitalPredict, recording, updateState]);

  const handleChunkReady = useCallback(
    (chunk: RecordingChunkReadyEvent) => {
      if (
        !isRunningRef.current &&
        !isScanEndingRef.current &&
        chunk.chunkIndex < chunkCountRef.current
      ) {
        return;
      }

      chunkCountRef.current = Math.max(
        chunkCountRef.current,
        chunk.chunkIndex + 1,
      );
      updateState({ chunkCount: chunkCountRef.current });

      // Match mobile: start quick-vitals polling as soon as chunk 5 is ready,
      // without waiting for S3 upload to finish.
      if (
        chunkCountRef.current === RPPG_QUICK_VITALS_START_CHUNK &&
        !quickVitalsTimerRef.current
      ) {
        scheduleQuickVitalsPoll();
      }

      if (
        chunkCountRef.current === RPPG_VITAL_PREDICT_CHUNK &&
        !vitalPredictFiredRef.current
      ) {
        void fireVitalPredict();
      }

      uploadQueueRef.current = uploadQueueRef.current.then(async () => {
        if (
          !isRunningRef.current &&
          !isScanEndingRef.current &&
          chunk.chunkIndex < chunkCountRef.current - 1
        ) {
          return;
        }

        try {
          const initResp = await ensureUploadInit(readingIdRef.current);
          const success = await uploadChunkWithRetry(
            initResp.upload.url,
            initResp.upload.fields,
            initResp.key_prefix,
            chunk.chunkIndex,
            chunk.blob,
          );

          if (success) {
            uploadedChunkCountRef.current += 1;
            updateState({
              uploadedChunkCount: uploadedChunkCountRef.current,
            });
          }
        } catch (err: unknown) {
          const message = err instanceof Error ? err.message : String(err);
          console.warn(
            `[rPPG] chunk ${chunk.chunkIndex} upload failed:`,
            message,
          );
        }
      });
    },
    [ensureUploadInit, fireVitalPredict, scheduleQuickVitalsPoll, updateState],
  );

  const reset = useCallback(() => {
    isRunningRef.current = false;
    isScanEndingRef.current = false;
    chunkCountRef.current = 0;
    uploadedChunkCountRef.current = 0;
    readingIdRef.current = '';
    initResponseRef.current = null;
    uploadInitPromiseRef.current = null;
    uploadMetadataRef.current = undefined;
    prefetchedReadingIdRef.current = '';
    vitalPredictFiredRef.current = false;
    pendingFinalResultRef.current = null;
    predictErrorRef.current = null;
    qualityAbortedRef.current = false;
    abortRecommendedStreakRef.current = 0;
    ignoreAbortRecommendedRef.current = false;
    qualityContinuePromptRef.current = false;
    uploadQueueRef.current = Promise.resolve();
    clearQuickVitalsTimer();
    setState(INITIAL_STATE);
  }, [clearQuickVitalsTimer]);

  const stopScan = useCallback(async () => {
    if (stoppingRef.current) {
      return;
    }
    stoppingRef.current = true;
    isRunningRef.current = false;
    isScanEndingRef.current = true;

    try {
      await recording.stop();
      clearQuickVitalsTimer();
      await uploadQueueRef.current;
      abortRecommendedStreakRef.current = 0;
      qualityContinuePromptRef.current = false;
      updateState({
        phase: 'idle',
        error: null,
        progress: 0,
        finalResult: null,
        captureQuality: null,
        qualityContinuePrompt: null,
      });
    } finally {
      stoppingRef.current = false;
    }
  }, [clearQuickVitalsTimer, recording, updateState]);

  const start = useCallback(
    async (demographics: RppgDemographics) => {
      if (isRunningRef.current) {
        return;
      }

      stoppingRef.current = false;
      isScanEndingRef.current = false;
      isRunningRef.current = true;
      qualityAbortedRef.current = false;
      abortRecommendedStreakRef.current = 0;
      ignoreAbortRecommendedRef.current = false;
      qualityContinuePromptRef.current = false;
      vitalPredictFiredRef.current = false;
      chunkCountRef.current = 0;
      uploadedChunkCountRef.current = 0;
      demographicsRef.current = demographics;
      predictErrorRef.current = null;
      pendingFinalResultRef.current = null;
      uploadQueueRef.current = Promise.resolve();

      const rid =
        prefetchedReadingIdRef.current ||
        initResponseRef.current?.reading_id ||
        createReadingId();
      prefetchedReadingIdRef.current = '';
      readingIdRef.current = rid;

      updateState({
        phase: 'initializing',
        readingId: rid,
        chunkCount: 0,
        uploadedChunkCount: 0,
        progress: 0,
        processingProgress: 0,
        liveVitals: null,
        captureQuality: null,
        qualityAbort: null,
        qualityContinuePrompt: null,
        finalResult: null,
        error: null,
        imageValidityCounts: {},
      });

      try {
        await ensureUploadInit(rid);
        await recording.start(
          { chunkDurationMs: RPPG_CHUNK_DURATION_MS },
          RPPG_SCAN_DURATION_MS,
        );
        updateState({ phase: 'scanning' });
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : String(err);
        isRunningRef.current = false;
        updateState({ phase: 'error', error: message });
        throw err;
      }
    },
    [ensureUploadInit, recording, updateState],
  );

  useEffect(() => {
    return recording.onChunkReady(handleChunkReady);
  }, [recording, handleChunkReady]);

  useEffect(() => {
    const unsubProgress = subscribe(
      MedsiWebEvents.RECORDING_PROGRESS,
      ({ progress }) => {
        if (isRunningRef.current) {
          updateState({ progress });
        }
      },
    );

    const unsubFace = subscribe(
      MedsiWebEvents.FACE_VALIDATION,
      (event: FaceValidationEvent) => {
        if (!isRunningRef.current) {
          updateState({ faceValidity: event });
          return;
        }

        setState(prev => {
          const counts = { ...prev.imageValidityCounts };
          if (event.validity) {
            counts[event.validity] = (counts[event.validity] ?? 0) + 1;
          }
          return {
            ...prev,
            faceValidity: event,
            imageValidityCounts: counts,
          };
        });
      },
    );

    const unsubError = subscribe(MedsiWebEvents.ERROR, ({ message }) => {
      if (isRunningRef.current) {
        updateState({ error: message });
      }
    });

    return () => {
      unsubProgress();
      unsubFace();
      unsubError();
    };
  }, [updateState]);

  useEffect(() => {
    if (state.qualityContinuePrompt) {
      return;
    }
    if (
      state.progress >= 1 &&
      state.phase === 'scanning' &&
      isRunningRef.current &&
      !isScanEndingRef.current
    ) {
      void transitionToProcessing();
    }
  }, [
    state.progress,
    state.phase,
    state.qualityContinuePrompt,
    transitionToProcessing,
  ]);

  return {
    ...state,
    start,
    stopScan,
    reset,
    prefetchUploadInit,
    abortScan,
    clearQualityAbort,
    clearQualityContinuePrompt,
    continueScanDespiteAbort,
  };
}
